import { formatUtcOffset } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Button,
  Modal,
  Popover,
  ScrollArea,
  SegmentedControl,
  Select,
  Skeleton,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconInfoCircleFilled,
  IconLock,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDayLong } from '../../format.js';
import { TimeInput } from '../../inputs/TimeInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { PeriodNav } from './PeriodNav.js';
import classes from './RestDayEditor.module.css';
import { daySegments, dayTotals, formatMinutes, shiftDate } from './rest-hours.js';
import { ActivityDot, EMPTY, RestNotice, restTable } from './rest-ui.js';
import {
  type ClockChangeView,
  clockText,
  daySpan,
  offsetAt,
  type ShipTime,
  wallClock,
} from './ship-clock.js';
import { errorCode, type RestHoursEditor } from './source.js';
import type { RestMonth, WorkPeriod } from './types.js';
import {
  fromWorkPeriod,
  periodChoices,
  toWorkPeriodInput,
  WORK_ACTIVITY_OPTIONS,
  type WorkPeriodValues,
  workPeriodSchema,
} from './work-period-form.js';

export interface RestDayEditorProps {
  opened: boolean;
  onClose: () => void;
  editor: RestHoursEditor;
  /** Kimin dönemleri: kaptan başkası adına girer, kişi kendi adına (yetki gemi sunucusunda). */
  userId: string;
  fullName: string;
  rank: string | null;
  day: string;
  /** Gemi yerel bugünü: ileri gün seçilmez. */
  today: string;
  /** Penceredeki ‹ › ile gün değişir; ekran günün dönemlerini yeniden okur (G3-27). */
  onDayChange: (day: string) => void;
  /** Gemi saati: dilim ya da saat kaydının çizelgesi (ADR 0065 §4). */
  timeZone: ShipTime;
  /** Onay bekleyen saat değişiklikleri: girilen saat sonrasındaysa uyarı (ADR 0065 §3). */
  pendingClock?: readonly ClockChangeView[];
  /** Günün dönemleri; okunurken `null`. */
  periods: readonly WorkPeriod[] | null;
  /** Dönemler okunamadıysa hata kodu (boş gün sanılmaz; G3-29). */
  error: string | null;
  onRetry: () => void;
  /** Günün ayının kaydı: imzalı ay kilitlidir (ADR 0021 §5; G3-28). */
  month: RestMonth | null;
  /** Yazma sonrası ekran raporu ve dönemleri yeniden okur. */
  onChanged: () => void;
}

const EMPTY_VALUES = (day: string): WorkPeriodValues => ({
  date: day,
  startTime: '',
  endTime: '',
  activity: 'watch',
  location: '',
  note: '',
});

/** Pencerenin bir satırı: çalışma dönemi (düzeltilebilir) ya da aradaki dinlenme (salt okunur). */
type Row =
  | {
      kind: 'work';
      period: WorkPeriod;
      start: string;
      end: string;
      minutes: number;
      /** Duvar saati farkı gerçek süreden farklıysa görünen süre (ADR 0065 §7). */
      shown: number | null;
      /** Girildiği fark saat kaydının farkından farklıysa (ADR 0065 §6). */
      enteredAt: number | null;
      fromPreviousDay: boolean;
      toNextDay: boolean;
    }
  | { kind: 'rest'; from: number; to: number };

/**
 * Günlük çalışma ve dinlenme kaydı (M10d2; BGM "Daily Schedule" › "Edit";
 * `gemi-calisma-dinlenme-saatleri.jpeg` "Daily Working & Rest Record"): gün 00:00–24:00 okunur,
 * dinlenme satırları gri ve eylemsiz; dönem eklenir, düzeltilir, kaldırılır. Saatler gemi saatiyle
 * girilir; kurallar (çakışma, görev, imzalı ayın kilidi) gemi sunucusunda yeniden denetlenir ve
 * hata kodu burada gösterilir [MLC A2.3 §12; ADR 0021 §5].
 */
export function RestDayEditor(props: RestDayEditorProps) {
  const {
    opened,
    onClose,
    editor,
    userId,
    fullName,
    rank,
    day,
    today,
    onDayChange,
    timeZone,
    pendingClock,
    periods,
    error: loadError,
    onRetry,
    month,
    onChanged,
  } = props;
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState<WorkPeriod | 'new' | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<WorkPeriodValues>({
    resolver: zodResolver(workPeriodSchema),
    defaultValues: EMPTY_VALUES(day),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  // Kaydedilmemiş dönem formu varken kabuktan gezinme onay ister (G3-33)
  useUnsavedChanges(opened && editing !== null && isDirty);
  const startRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const pencils = useRef(new Map<string, HTMLButtonElement>());
  const returnFocus = useRef<string | 'add' | null>(null);

  // İmzalı ya da onay bekleyen ay kilitli: kaldırma yok; düzeltme yalnız onay yetkilisine,
  // gerekçe notuyla (ADR 0021 §5; sunucu `assertRestMonthsOpen`)
  const locked = month?.state.status === 'pending' || month?.state.status === 'approved';

  // Gün sınırları gemi saati kaydından (ADR 0065 §4)
  const span = useMemo(() => daySpan(timeZone, day), [timeZone, day]);
  const dayStart = span.start;
  const dayEnd = span.end;
  /** Anın saati, tekrar eden saatte oluşumuyla ("23:30 (2nd)"). */
  const timeText = (at: number) => {
    const wall = wallClock(timeZone, at);
    return wall.occurrence
      ? `${wall.time} ${t(`restHours.clock.occurrence${wall.occurrence}`)}`
      : wall.time;
  };
  const segments = useMemo(
    () => (periods ? daySegments(periods, timeZone, day) : []),
    [periods, timeZone, day],
  );
  const totals = dayTotals(segments);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `timeText` yalnız `timeZone`'a bağlı
  const rows = useMemo<Row[]>(() => {
    if (!periods) return [];
    const ofDay = periods
      .filter((p) => Date.parse(p.start) < dayEnd && Date.parse(p.end) > dayStart)
      .sort((a, b) => a.start.localeCompare(b.start));
    const work: Row[] = ofDay.map((period) => {
      const startAt = Date.parse(period.start);
      const endAt = Date.parse(period.end);
      const minutes = Math.round((endAt - startAt) / 60_000);
      // Görünen süre: duvar saatleri farkı (bitiş anı önceki farkla: 24:00 (1.))
      const shown = Math.round(
        (endAt +
          offsetAt(timeZone, endAt - 1) * 60_000 -
          (startAt + offsetAt(timeZone, startAt) * 60_000)) /
          60_000,
      );
      const current = offsetAt(timeZone, startAt);
      return {
        kind: 'work' as const,
        period,
        start: timeText(startAt),
        end: timeText(endAt),
        minutes,
        shown: shown === minutes ? null : shown,
        enteredAt:
          period.tzOffsetMin !== undefined && period.tzOffsetMin !== current
            ? period.tzOffsetMin
            : null,
        fromPreviousDay: startAt < dayStart,
        toNextDay: endAt > dayEnd,
      };
    });
    const rest: Row[] = segments
      .filter((segment) => segment.kind === 'rest')
      .map((segment) => ({ kind: 'rest', from: segment.from, to: segment.to }));
    const position = (row: Row) =>
      row.kind === 'rest'
        ? row.from
        : Math.max(0, Math.round((Date.parse(row.period.start) - dayStart) / 60_000));
    return [...work, ...rest].sort((x, y) => position(x) - position(y));
  }, [periods, segments, timeZone, dayStart, dayEnd]);
  const dayLength = span.minutes;
  /** Gün başından dakikanın saati; gün sonu 24:00. */
  const restText = (minute: number) =>
    minute >= dayLength ? clockText(1440) : timeText(dayStart + minute * 60_000);
  // Tekrar eden saatte "hangisi" ve onay bekleyen değişiklik uyarısı (ADR 0065 §3, §7)
  const watched = form.watch(['date', 'startTime', 'endTime', 'startOccurrence']);
  const choices = periodChoices(
    {
      date: watched[0] ?? day,
      startTime: watched[1] ?? '',
      endTime: watched[2] ?? '',
      ...(watched[3] ? { startOccurrence: watched[3] } : {}),
    },
    timeZone,
  );
  const pendingAfter = (pendingClock ?? []).find((change) => {
    const at = Date.parse(change.effectiveAt);
    return [...choices.starts, ...choices.ends].some((instant) => instant >= at);
  });
  const message = (code: string) =>
    t(`restHours.edit.errors.${code}`, { defaultValue: t('restHours.edit.errors.GENERIC') });

  // Form açılınca odak başlangıç saatinde; kapanınca açan düğmeye döner (G3-31)
  useEffect(() => {
    if (editing !== null) {
      requestAnimationFrame(() => startRef.current?.focus());
      return;
    }
    const target = returnFocus.current;
    returnFocus.current = null;
    if (!target) return;
    requestAnimationFrame(() =>
      (target === 'add' ? addRef.current : pencils.current.get(target))?.focus(),
    );
  }, [editing]);

  const open = (target: WorkPeriod | 'new') => {
    setError(null);
    setRemoving(null);
    form.reset(target === 'new' ? EMPTY_VALUES(day) : fromWorkPeriod(target, timeZone));
    setEditing(target);
  };
  const closeForm = () => {
    returnFocus.current = editing === 'new' ? 'add' : editing ? editing.id : null;
    setError(null);
    setEditing(null);
  };

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    // İmzalı ayda düzeltme gerekçeyle (sunucudaki kuralın arayüzdeki karşılığı; daha sıkı)
    if (locked && values.note.trim() === '') {
      form.setError('note', { message: 'restHours.edit.errors.MONTH_LOCK_REASON_REQUIRED' });
      return;
    }
    const converted = toWorkPeriodInput(values, { timeZone, now: new Date().toISOString() });
    if (!converted.ok) {
      form.setError(converted.field, { message: `restHours.edit.errors.${converted.code}` });
      return;
    }
    try {
      if (editing === 'new') {
        await editor.addWorkPeriod({ ...converted.input, userId });
      } else if (editing) {
        await editor.updateWorkPeriod(editing.id, {
          ...converted.input,
          rowVersion: editing.rowVersion,
        });
      }
      returnFocus.current = editing === 'new' ? 'add' : editing ? editing.id : null;
      // Kayıttan sonra form temiz: gezinme onayı istenmez
      form.reset(values);
      setEditing(null);
      onChanged();
    } catch (failure) {
      setError(errorCode(failure));
    }
  });

  const remove = async (period: WorkPeriod) => {
    setError(null);
    setBusy(period.id);
    try {
      await editor.removeWorkPeriod(period.id, period.rowVersion);
      setRemoving(null);
      onChanged();
    } catch (failure) {
      setRemoving(null);
      setError(errorCode(failure));
    } finally {
      setBusy(null);
    }
  };

  const timePart = (field: 'start' | 'end', part: 'hours' | 'minutes') =>
    t('restHours.edit.timePart', {
      field: t(`restHours.columns.${field}`),
      part: t(`common.${part}`),
    });
  const fieldError = (key: keyof WorkPeriodValues) => {
    const value = errors[key]?.message;
    return value ? t(value) : undefined;
  };
  const close = () => {
    // Pencere her açılışta temiz başlar: eski hata ve kaldırma onayı kalmaz (Copilot, #58)
    setEditing(null);
    setRemoving(null);
    setError(null);
    form.reset(EMPTY_VALUES(day));
    onClose();
  };
  const rowsDisabled = busy !== null || editing !== null;
  const lockedTip = (node: ReactNode, label: string) =>
    locked ? (
      <Tooltip label={label} withArrow multiline w={240}>
        <span className={classes.tipTarget}>{node}</span>
      </Tooltip>
    ) : (
      node
    );

  const body =
    periods === null ? (
      loadError ? (
        <EmptyState
          tone="danger"
          title={t('restHours.periodsError')}
          description={message(loadError)}
          action={
            <Button size="xs" variant="light" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : (
        <div className={classes.skeleton} aria-hidden>
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} height={28} />
          ))}
        </div>
      )
    ) : (
      <ScrollArea.Autosize
        mah="clamp(140px, calc(100dvh - 520px), 360px)"
        type="auto"
        // Kayan bölge klavyeyle de kaydırılabilsin (WCAG 2.1.1): satır eylemleri kapalıyken de
        viewportProps={{
          tabIndex: 0,
          role: 'region',
          'aria-label': t('restHours.periodsTable'),
        }}
      >
        <table className={`${restTable.table} ${classes.table}`}>
          <thead>
            <tr>
              <th scope="col">{t('restHours.columns.start')}</th>
              <th scope="col">{t('restHours.columns.end')}</th>
              <th scope="col">{t('restHours.columns.duration')}</th>
              <th scope="col">{t('restHours.columns.activity')}</th>
              <th scope="col">{t('restHours.columns.location')}</th>
              <th scope="col">{t('restHours.columns.note')}</th>
              <th scope="col">
                <span className={classes.hidden}>{t('restHours.edit.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              if (row.kind === 'rest') {
                return (
                  <tr key={`rest-${row.from}`} className={restTable.restRow}>
                    <td className={restTable.num}>{restText(row.from)}</td>
                    <td className={restTable.num}>{restText(row.to)}</td>
                    <td className={restTable.num}>{formatMinutes(row.to - row.from)}</td>
                    <td>
                      <span className={restTable.activity}>
                        <ActivityDot activity={null} />
                        {t('restHours.activity.rest')}
                      </span>
                    </td>
                    <td>{EMPTY}</td>
                    <td>{EMPTY}</td>
                    <td />
                  </tr>
                );
              }
              const { period } = row;
              const range = { from: row.start, to: row.end };
              return (
                <tr key={period.id} data-editing={editing === period ? true : undefined}>
                  <td className={restTable.num}>
                    {row.start}
                    {row.fromPreviousDay ? (
                      <small className={classes.dayMark}>{t('restHours.edit.previousDay')}</small>
                    ) : null}
                  </td>
                  <td className={restTable.num}>
                    {row.end}
                    {row.toNextDay ? <small className={classes.dayMark}>+1</small> : null}
                  </td>
                  <td className={restTable.num}>
                    {row.shown === null ? (
                      formatMinutes(row.minutes)
                    ) : (
                      // Saat değiştiyse gerçek süre ve görünen süre (ADR 0065 §7)
                      <>
                        {t('restHours.clock.actual', { value: formatMinutes(row.minutes) })}
                        <small className={classes.dayMark}>
                          {t('restHours.clock.shown', { value: formatMinutes(row.shown) })}
                        </small>
                      </>
                    )}
                    {row.enteredAt !== null ? (
                      <small className={classes.dayMark}>
                        {t('restHours.clock.enteredAt', { offset: formatUtcOffset(row.enteredAt) })}
                      </small>
                    ) : null}
                  </td>
                  <td>
                    <span className={restTable.activity}>
                      <ActivityDot activity={period.activity} />
                      {t(`restHours.activity.${period.activity}`, {
                        defaultValue: period.activity,
                      })}
                    </span>
                  </td>
                  <td className={restTable.secondary}>{period.location ?? EMPTY}</td>
                  <td className={`${restTable.secondary} ${classes.note}`}>
                    {period.note ?? EMPTY}
                  </td>
                  <td className={classes.actions}>
                    <span className={classes.actionGroup}>
                      <Tooltip label={t('restHours.edit.editPeriod', range)} withArrow>
                        <ActionIcon
                          ref={(node) => {
                            if (node) pencils.current.set(period.id, node);
                            else pencils.current.delete(period.id);
                          }}
                          variant="subtle"
                          size="sm"
                          aria-label={t('restHours.edit.editPeriod', range)}
                          disabled={rowsDisabled}
                          onClick={() => open(period)}
                        >
                          <IconPencil size={15} />
                        </ActionIcon>
                      </Tooltip>
                      <Popover
                        opened={removing === period.id}
                        onChange={(value) => {
                          if (!value) setRemoving(null);
                        }}
                        position="bottom-end"
                        withArrow
                        trapFocus
                        returnFocus
                        withinPortal
                      >
                        <Popover.Target>
                          {lockedTip(
                            <Tooltip
                              label={t('restHours.edit.removePeriod', range)}
                              withArrow
                              disabled={locked}
                            >
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                aria-label={t('restHours.edit.removePeriod', range)}
                                disabled={rowsDisabled || locked}
                                onClick={() => setRemoving(period.id)}
                              >
                                <IconTrash size={15} />
                              </ActionIcon>
                            </Tooltip>,
                            t('restHours.edit.lockedRemove'),
                          )}
                        </Popover.Target>
                        <Popover.Dropdown
                          className={classes.confirm}
                          aria-label={t('restHours.edit.removePeriod', range)}
                        >
                          <Text fz="sm" mb={10}>
                            {t('restHours.edit.removeQuestion', range)}
                          </Text>
                          <span className={classes.confirmActions}>
                            <Button
                              size="compact-sm"
                              variant="default"
                              onClick={() => setRemoving(null)}
                            >
                              {t('restHours.edit.cancel')}
                            </Button>
                            <Button
                              size="compact-sm"
                              color="red"
                              loading={busy === period.id}
                              onClick={() => void remove(period)}
                            >
                              {t('restHours.edit.confirmRemove')}
                            </Button>
                          </span>
                        </Popover.Dropdown>
                      </Popover>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className={classes.totalLabel}>
                {t('restHours.edit.totalWork')}
              </td>
              <td colSpan={5} className={`${restTable.num} ${classes.totalValue}`}>
                {formatMinutes(totals.work)}
              </td>
            </tr>
            <tr>
              <td colSpan={2} className={classes.totalLabel}>
                {t('restHours.edit.totalRest')}
              </td>
              <td colSpan={5} className={`${restTable.num} ${classes.totalValue}`}>
                {formatMinutes(totals.rest)}
              </td>
            </tr>
          </tfoot>
        </table>
      </ScrollArea.Autosize>
    );

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={t('restHours.edit.title')}
      closeButtonProps={{ 'aria-label': t('restHours.edit.close') }}
      size={880}
      classNames={{ body: classes.modalBody, header: classes.modalHeader }}
    >
      <div className={classes.sub}>
        <PeriodNav
          label={formatDayLong(day, i18n.resolvedLanguage ?? 'en')}
          previousLabel={t('restHours.previousDay')}
          nextLabel={t('restHours.nextDay')}
          onPrevious={() => onDayChange(shiftDate(day, -1))}
          onNext={() => onDayChange(shiftDate(day, 1))}
          previousDisabled={editing !== null}
          nextDisabled={editing !== null || day >= today}
        />
        <span className={classes.who}>
          {rank ? t('restHours.edit.who', { name: fullName, rank }) : fullName}
        </span>
      </div>

      <div className={classes.content}>
        {locked ? (
          <RestNotice tone="warning" icon={<IconLock size={18} />} role="status">
            <p className={classes.noticeTitle}>{t('restHours.edit.lockedTitle')}</p>
            <p>{t('restHours.edit.lockedText')}</p>
          </RestNotice>
        ) : null}
        {error ? (
          <RestNotice tone="danger" icon={<IconAlertCircle size={18} />} role="alert">
            {message(error)}
          </RestNotice>
        ) : null}

        <div className={restTable.frame}>{body}</div>

        {editing !== null ? (
          <form id="rest-period-form" onSubmit={submit} noValidate className={classes.form}>
            <p className={classes.formTitle}>
              {editing === 'new' ? t('restHours.edit.addTitle') : t('restHours.edit.editTitle')}
            </p>
            {pendingAfter ? (
              <RestNotice tone="warning" icon={<IconAlertCircle size={18} />} role="status">
                {t('restHours.clock.formPending', {
                  time: clockText(
                    Math.round(
                      (((Date.parse(pendingAfter.effectiveAt) / 60_000 +
                        pendingAfter.oldOffsetMin) %
                        1440) +
                        1440) %
                        1440,
                    ),
                  ),
                  offset: formatUtcOffset(pendingAfter.oldOffsetMin),
                })}
              </RestNotice>
            ) : null}
            <div className={classes.grid}>
              <Controller
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <TimeInput
                    label={t('restHours.columns.start')}
                    // İki saat alanında saat ve dakika girişleri ayrı adlı (ekran okuyucu)
                    hoursInputLabel={timePart('start', 'hours')}
                    minutesInputLabel={timePart('start', 'minutes')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    hoursRef={startRef}
                    error={fieldError('startTime')}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <TimeInput
                    label={t('restHours.columns.end')}
                    hoursInputLabel={timePart('end', 'hours')}
                    minutesInputLabel={timePart('end', 'minutes')}
                    description={t('restHours.edit.nextDayHint')}
                    inputWrapperOrder={['label', 'input', 'description', 'error']}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={fieldError('endTime')}
                  />
                )}
              />
              {choices.starts.length > 1 ? (
                <Controller
                  control={form.control}
                  name="startOccurrence"
                  render={({ field }) => (
                    <div>
                      <span className={classes.occurrenceLabel} id="start-occurrence">
                        {t('restHours.clock.whichTime', { time: watched[1] })}
                      </span>
                      <SegmentedControl
                        aria-labelledby="start-occurrence"
                        value={String(field.value ?? 1)}
                        onChange={(value) => field.onChange(Number(value) as 1 | 2)}
                        data={[
                          { value: '1', label: t('restHours.clock.first') },
                          { value: '2', label: t('restHours.clock.second') },
                        ]}
                      />
                    </div>
                  )}
                />
              ) : null}
              {choices.ends.length > 1 ? (
                <Controller
                  control={form.control}
                  name="endOccurrence"
                  render={({ field }) => (
                    <div>
                      <span className={classes.occurrenceLabel} id="end-occurrence">
                        {t('restHours.clock.whichTime', { time: watched[2] })}
                      </span>
                      <SegmentedControl
                        aria-labelledby="end-occurrence"
                        value={String(field.value ?? 1)}
                        onChange={(value) => field.onChange(Number(value) as 1 | 2)}
                        data={[
                          { value: '1', label: t('restHours.clock.first') },
                          { value: '2', label: t('restHours.clock.second') },
                        ]}
                      />
                    </div>
                  )}
                />
              ) : null}
              <Controller
                control={form.control}
                name="activity"
                render={({ field }) => (
                  <Select
                    label={t('restHours.columns.activity')}
                    data={WORK_ACTIVITY_OPTIONS.map((value) => ({
                      value,
                      label: t(`restHours.activity.${value}`),
                    }))}
                    value={field.value}
                    onChange={(value) => field.onChange(value ?? 'watch')}
                    allowDeselect={false}
                    comboboxProps={{ withinPortal: true }}
                    error={fieldError('activity')}
                  />
                )}
              />
              <TextInput
                label={t('restHours.columns.location')}
                error={fieldError('location')}
                {...form.register('location')}
              />
              <Textarea
                className={classes.full}
                label={t('restHours.columns.note')}
                description={
                  locked ? t('restHours.edit.lockedNoteHint') : t('restHours.edit.noteHint')
                }
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                autosize
                minRows={2}
                maxRows={4}
                error={fieldError('note')}
                {...form.register('note')}
              />
            </div>
          </form>
        ) : null}

        <RestNotice tone="info" icon={<IconInfoCircleFilled size={18} />}>
          {t('restHours.edit.mlcNote')}
        </RestNotice>
      </div>

      <div className={classes.footer}>
        {editing !== null ? (
          <>
            <Button variant="default" onClick={closeForm}>
              {t('restHours.edit.cancel')}
            </Button>
            <Button type="submit" form="rest-period-form" loading={isSubmitting}>
              {t('restHours.edit.save')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="default" onClick={close}>
              {t('restHours.edit.done')}
            </Button>
            <Button
              ref={addRef}
              leftSection={<IconPlus size={16} />}
              disabled={periods === null || busy !== null}
              onClick={() => open('new')}
            >
              {t('restHours.edit.add')}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
